// Sabit Rapor · 5550 — ST Rut Tanım Listesi (Raporlar → Sabit Raporlar → ST Rut Tanım Listesi).
//
// İki ekran:
//   1) RouteDefinitionCriteriaPage — kriter formu (hepsi opsiyonel): Distribütör Kod, Satış
//      Temsilcisi, Rut Tanım, Esas Alınacak Tarih (varsayılan bugün), ST Çalışma Tipi. Kriterler
//      değiştikçe "N rut · M nokta kritere uyuyor" canlı önizlemesi gösterilir. "Raporu Göster"
//      ile ayrı pencerede (?embed=true) liste açılır.
//   2) RouteDefinitionListPage — /api/detail-reports/route-definitions'tan rut × müşteri düz
//      liste (RoutePlan + RoutePlanCustomer/Interval + Vendor + Customer GERÇEK verisi).
//      Kolonlar DB'ye göre: ST (plana atanmış satış temsilcisi), Distribütör, Rut Kodu,
//      Rut Açıklama, Başlangıç, Frekans, Bitiş, Frekans Birimi, Gün, Ziyaret Sırası,
//      Müşteri Ünvanı, Toplam Nokta.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams} from 'react-router-dom';
import { authHeaders, exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { DateInput } from './reports/DateInput';
import { MultiSelect } from './reports/MultiSelect';

const ENDPOINT = '/api/detail-reports/route-definitions';
const REPORT_CODE = '5550';
const REPORT_TITLE = 'ST Rut Tanım Listesi';

// ── Yardımcılar ──
function normalizeList(payload) { if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return []; }
const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k];
  return undefined; };
const optId = (o) => pick(o, 'id', 'Id');
const optCode = (o) => pick(o, 'textId', 'TextId', 'erpId', 'ErpId');
const optName = (o) => pick(o, 'name', 'Name', 'description', 'Description', 'title', 'Title');
const optLabel = (o) => { const code = optCode(o);
  const name = optName(o);
  if (code && name) return `${code} - ${name}`;
  return name || code || '—'; };
// Lookup listesini MultiSelect'in beklediği {value,label} şekline çevirir.
const asChoices = (arr) => (arr || []).map((o) => ({ value: String(optId(o)), label: optLabel(o) }));
const nfInt = new Intl.NumberFormat('tr-TR');
const fmtInt = (v) => nfInt.format(Number(v) || 0);

function today() { const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
// ISO "yyyy-MM-dd" → "dd.MM.yyyy" (backend tarih kolonları ISO döner; sıralama ISO ile kronolojik).
const fmtDate = (iso) => { if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return d ? `${d}.${m}.${y}` : iso; };

// Distribütör / Temsilci / ST Çalışma Tipi / Rut Tanım lookup'ları.
function useLookups() { const [lk, setLk] = useState({ vendors: [], reps: [], repTypes: [], plans: [] });
  useEffect(() => { const headers = authHeaders();
    const load = (url) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then(normalizeList).catch(() => []);
    // Bağımsız yükle — biri (ör. route-plans) ağır/askıda kalırsa diğerleri beklemesin.
    load('/api/vendors/Select').then((vendors) => setLk((s) => ({ ...s, vendors })));
    load('/api/representatives/Select').then((reps) => setLk((s) => ({ ...s, reps })));
    load('/api/representativetypes/Select').then((repTypes) => setLk((s) => ({ ...s, repTypes })));
    load('/api/route-plans?take=1000').then((plans) => setLk((s) => ({ ...s, plans }))); }, []);
  return lk; }

/* ════════════════════════════ STYLES ════════════════════════════ */
const c = { page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  hTitle: { fontSize: 22, fontWeight: 800, color: 'var(--text-primary, #0f172a)', margin: 0 },
  hCrumb: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #64748b)', marginTop: 2 },
  card: { background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 18, boxShadow: 'var(--shadow-card)', padding: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardHeadIcon: { width: 40, height: 40, borderRadius: 12, background: 'var(--bg-input, #eef2ff)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent, #0073ea)' },
  cardTitle: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--text-secondary, #475569)' },
  input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13.5,
    width: '100%', boxSizing: 'border-box' },
  btn: { padding: '11px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost: { background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)' },
  btnPrimary: { background: 'var(--accent-strong, #1e3a8a)', color: '#fff' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '44px 24px', textAlign: 'center' },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, background: 'var(--bg-input, #eef2f7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', marginBottom: 6 },
  countChip: { marginTop: 4, padding: '8px 16px', borderRadius: 999, background: 'var(--bg-input, #eef2ff)',
    color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 13.5 } };

function Field({ label, children }) { return (
    <label style={c.field}>
      <span style={c.label}>{label}</span>
      {children}
    </label>
  ); }
function Select({ value, onChange, options, placeholder = 'Tümü' }) { return (
    <select style={c.input} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(o.value ?? optId(o))} value={String(o.value ?? optId(o))}>
          {o.label !== undefined ? o.label : optLabel(o)}
        </option>
      ))}
    </select>
  ); }

/* ═══════════════════════ 1) KRİTER EKRANI ═══════════════════════ */
export function RouteDefinitionCriteriaPage() { usePageMeta(REPORT_TITLE, "Sabit Raporlar › Satış Organizasyonu");
  const lk = useLookups();
  const [vendorId, setVendorId] = useState([]);
  const [repId, setRepId] = useState([]);
  const [planId, setPlanId] = useState([]);
  const [refDate, setRefDate] = useState(today);
  const [repTypeId, setRepTypeId] = useState([]);
  const [preview, setPreview] = useState(null);

  // Seçilen distribütöre ait temsilciler (rep.vendorId ile client-side filtre).
  const repOptions = useMemo(() => { const forVendor = vendorId
      ? lk.reps.filter((r) => String(pick(r, 'vendorId', 'VendorId')) === String(vendorId))
      : lk.reps;
    return forVendor.map((r) => ({ value: String(optId(r)), label: optLabel(r) })); }, [lk.reps, vendorId]);
  const planOptions = useMemo(
    () => lk.plans.map((p) => ({ value: String(optId(p)), label: optLabel(p) })),
    [lk.plans],
  );
  const repTypeOptions = useMemo(
    () => lk.repTypes.map((t) => ({ value: String(optId(t)), label: optName(t) || '—' })),
    [lk.repTypes],
  );

  const buildParams = useCallback((extra) => { const p = new URLSearchParams();
    if (vendorId.length) p.set('vendorId', vendorId.join(','));
    if (repId.length) p.set('representativeId', repId.join(','));
    if (planId.length) p.set('routePlanId', planId.join(','));
    if (refDate) p.set('refDate', refDate);
    if (repTypeId.length) p.set('representativeTypeId', repTypeId.join(','));
    if (extra) Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p; }, [vendorId, repId, planId, refDate, repTypeId]);

  // Canlı önizleme sayacı (previewOnly — yalnız sayaç, satır materyalize edilmez).
  useEffect(() => { let cancelled = false;
    const p = buildParams({ previewOnly: 'true' });
    fetch(`${ENDPOINT}?${p.toString()}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled) setPreview((j?.data ?? j)?.totals ?? null); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; }; }, [buildParams]);

  const clear = () => { setVendorId([]); setRepId([]); setPlanId([]); setRepTypeId([]); setRefDate(today()); };

  const showReport = () => { const p = buildParams({ embed: 'true' });
    const w = Math.min(1600, window.screen.availWidth - 40);
    const h = Math.min(940, window.screen.availHeight - 60);
    const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
    window.open(`/reports/st-route-plans-list?${p.toString()}`, 'stRutTanim5550',
      `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`); };

  return (
    <div style={c.page}>

      <div style={c.card}>
        <div style={c.cardHead}>
          <div style={c.cardHeadLeft}>
            <span style={c.cardHeadIcon}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </span>
            <span style={c.cardTitle}>Kriterler</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={{ ...c.btn, ...c.btnGhost }} onClick={clear}>✕ Temizle</button>
            <button type="button" style={{ ...c.btn, ...c.btnPrimary }} onClick={showReport}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              Raporu Göster
            </button>
          </div>
        </div>

        <div style={c.grid}>
          <Field label="Distribütör Kod">
            <MultiSelect value={vendorId} onChange={(v) => { setVendorId(v); setRepId([]); }} options={asChoices(lk.vendors)} />
          </Field>
          <Field label="Satış Temsilcisi">
            <MultiSelect value={repId} onChange={setRepId} options={repOptions} />
          </Field>
          <Field label="Rut Tanım">
            <MultiSelect value={planId} onChange={setPlanId} options={planOptions} />
          </Field>
          <Field label="Esas Alınacak Tarih">
            <DateInput value={refDate} onChange={(v) => setRefDate(v)} />
          </Field>
          <Field label="ST Çalışma Tipi">
            <MultiSelect value={repTypeId} onChange={setRepTypeId} options={repTypeOptions} />
          </Field>
        </div>
      </div>

      <div style={c.card}>
        <div style={c.emptyState}>
          <span style={c.emptyIcon}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="4" width="3" height="14" /></svg>
          </span>
          <strong style={{ fontSize: 17, color: 'var(--text-primary, #0f172a)' }}>Rapor ayrı ekranda görüntülenir</strong>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary, #64748b)', maxWidth: 560 }}>
            Kriterleri seçin ve <strong>Raporu Göster</strong>’e basın — rapor tam ekran açılır, oradan Excel olarak indirebilirsiniz.
          </span>
          {preview && (
            <span style={c.countChip}>{fmtInt(preview.rutSayisi)} rut · {fmtInt(preview.satirSayisi)} nokta kritere uyuyor</span>
          )}
        </div>
      </div>
    </div>
  ); }

/* ═══════════════════════ 2) RAPOR EKRANI ═══════════════════════ */
// Özel hücre render'ları kolonun `cell` alanında; tarih kolonları fmt: fmtDate ile formatlanır
// (ham değer ISO kaldığından ReportGrid sıralaması kronolojik). İlk kolon sticky.
const COLUMNS = [
  { key: 'st', label: 'ST', sticky: true },
  { key: 'distributor', label: 'Distribütör' },
  { key: 'routeCode', label: 'Rut Kodu', cell: (row) => <span style={l.codeChip}>{row.routeCode ?? '—'}</span> },
  { key: 'routeName', label: 'Rut Açıklama' },
  { key: 'startDate', label: 'Başlangıç', fmt: fmtDate },
  { key: 'frequency', label: 'Frekans', num: true },
  { key: 'endDate', label: 'Bitiş', fmt: fmtDate },
  { key: 'frequencyUnit', label: 'Frekans Birimi' },
  { key: 'day', label: 'Gün', cell: (row) => (row.day && row.day !== '—' ? <span style={l.dayChip}>{row.day}</span> : '—') },
  { key: 'visitOrder', label: 'Ziyaret Sırası', cell: (row) => <span style={l.orderChip}>{row.visitOrder ?? '—'}</span> },
  { key: 'customerTitle', label: 'Müşteri Ünvanı' },
  { key: 'totalPoints', label: 'Toplam Nokta', num: true, fmt: fmtInt },
];

// Kolon filtre/sıralama/sayfalama ARTIK ReportGrid'in içinde (TanStack). Eski client-side
// rowMatches/pageWindow/NUM_OPS/PAGE_SIZES + cellText kaldırıldı — grid bunları kendi yönetiyor.

const l = { wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-page, #f1f5f9)' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '14px 20px', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #e5e7eb)',
    position: 'sticky', top: 0, zIndex: 5 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10,
    border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-input, #f8fafc)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 800, fontSize: 13, cursor: 'pointer' },
  titleIcon: { width: 42, height: 42, borderRadius: 12, background: 'var(--accent-strong, #1e3a8a)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  subtitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #64748b)' },
  iconBtn: { width: 42, height: 42, borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  critBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', flexWrap: 'wrap',
    background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #eef2f7)', fontSize: 12.5 },
  critChip: { padding: '6px 12px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 700 },
  tableScroll: { flex: 1, overflow: 'auto', padding: '10px 20px 20px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--bg-card, #fff)' },
  th: { position: 'sticky', top: 0, background: 'var(--bg-card, #f8fafc)', textAlign: 'left',
    padding: '11px 12px', fontWeight: 800, color: 'var(--text-secondary, #64748b)', fontSize: 11.5,
    borderBottom: '2px solid var(--border-color, #e5e7eb)', whiteSpace: 'nowrap', zIndex: 1 },
  td: { padding: '11px 12px', borderBottom: '1px solid var(--border-color, #eef2f7)', whiteSpace: 'nowrap', color: 'var(--text-primary, #0f172a)' },
  idx: { width: 34, textAlign: 'right', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
  empty: { padding: 60, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '12px 20px', background: 'var(--bg-card, #fff)', borderTop: '1px solid var(--border-color, #e5e7eb)', position: 'sticky', bottom: 0 },
  thInner: { display: 'flex', alignItems: 'center', gap: 6 },
  funnelBtn: (active) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0,
    borderRadius: 6, cursor: 'pointer', padding: 0,
    border: `1px solid ${active ? 'var(--accent, #0073ea)' : 'var(--border-color, #e5e7eb)'}`,
    background: active ? 'var(--accent, #0073ea)' : 'var(--bg-card, #fff)',
    color: active ? '#fff' : 'var(--text-secondary, #94a3b8)' }),
  dayChip: { padding: '3px 10px', borderRadius: 999, background: 'rgba(226,68,92,0.10)', color: '#dc2626', fontWeight: 700, fontSize: 12 },
  codeChip: { padding: '3px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 12.5 },
  orderChip: { padding: '3px 9px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 700, fontSize: 12 },
  backdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  popover: { position: 'fixed', zIndex: 41, width: 250, padding: 12, borderRadius: 12,
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    boxShadow: '0 12px 32px rgba(15,23,42,0.18)', display: 'flex', flexDirection: 'column', gap: 10 },
  popTitle: { fontSize: 12, fontWeight: 800, color: 'var(--text-secondary, #64748b)' },
  popInput: { padding: '9px 10px', borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  popBtns: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  popBtn: (primary) => ({ padding: '8px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 12.5,
    background: primary ? 'var(--accent-strong, #1e3a8a)' : 'var(--bg-input, #f1f5f9)',
    color: primary ? '#fff' : 'var(--text-secondary, #475569)' }),
  pageBtn: (active, disabled) => ({ minWidth: 34, height: 34, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)',
    background: active ? 'var(--accent-strong, #1e3a8a)' : 'var(--bg-card, #fff)',
    color: active ? '#fff' : 'var(--text-primary, #0f172a)', fontWeight: 800, fontSize: 13,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, padding: '0 8px' }),
  pageSizeSel: { height: 34, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 700, fontSize: 13, padding: '0 8px', marginLeft: 8, cursor: 'pointer' } };

const hesc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function printAsPdf({ rows, subtitle }) { const head = COLUMNS.map((col) => `<th style="text-align:${col.num ? 'right' : 'left'}">${hesc(col.label)}</th>`).join('');
  const body = rows.map((row, i) => { const tds = COLUMNS.map((col) => { const v = col.fmt ? col.fmt(row[col.key]) : row[col.key];
      return `<td style="text-align:${col.num ? 'right' : 'left'}">${hesc(v ?? '')}</td>`; }).join('');
    return `<tr><td style="text-align:right;color:#94a3b8">${i + 1}</td>${tds}</tr>`; }).join('');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${REPORT_TITLE}</title>
<style>
  *{box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:18px}
  .rhead{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #1e3a8a;padding-bottom:8px;margin-bottom:6px}
  .rhead h1{font-size:16px;margin:0;color:#1e3a8a}.rhead .meta{font-size:11px;color:#64748b;text-align:right}
  .crit{font-size:10.5px;color:#475569;margin:6px 0 10px}
  table{width:100%;border-collapse:collapse}
  th{background:#eef2f7;color:#475569;font-size:9.5px;text-transform:uppercase;padding:6px 7px;border-bottom:1.5px solid #cbd5e1}
  td{font-size:10.5px;padding:5px 7px;border-bottom:1px solid #eef2f7;white-space:nowrap}
  tr:nth-child(even) td{background:#fafcff}@page{size:A4 landscape;margin:12mm}
</style></head><body>
  <div class="rhead"><h1>${REPORT_TITLE}</h1><div class="meta">${rows.length} satır</div></div>
  <div class="crit"><strong>Kriterler:</strong> ${hesc(subtitle)}</div>
  <table><thead><tr><th style="text-align:right">#</th>${head}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1000); }; }

export function RouteDefinitionListPage() { usePageMeta(REPORT_TITLE, 'Sabit Rapor');
  const [sp] = useSearchParams();

  const params = useMemo(() => { const o = {};
    for (const k of ['vendorId', 'representativeId', 'routePlanId', 'refDate', 'representativeTypeId']) { const v = sp.get(k);
      if (v) o[k] = v; }
    return o; }, [sp]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Kolon sırası/filtre/sıralama/sayfalama ReportGrid'in içinde (kalıcı görünüm). Excel/PDF
  // dışa aktarımı kullanıcının GÖRDÜĞÜ kümeyi almalı → grid görünen satırları buraya bildirir.
  // null = grid henüz bildirmedi (Excel ham `rows`'a düşer).
  const [visibleRows, setVisibleRows] = useState(null);
  const [nowLabel] = useState(() => new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  const fetchData = useCallback(async () => { setLoading(true);
    setError(null);
    try { const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${ENDPOINT}?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rapor alınamadı (${res.status})`);
      const json = await res.json();
      const data = json?.data ?? json;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotals(data?.totals ?? null); } catch (e) { setError(e.message || 'Hata');
      setRows([]); } finally { setLoading(false); } }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Grid görünen satırları bildirir (filtre + sıralama uygulanmış); Excel/PDF bunu kullanır.
  const exportRows = visibleRows ?? rows;

  const rutSayisi = totals?.rutSayisi ?? 0;
  const summaryText = `Esas Alınacak Tarih: ${fmtDate(params.refDate) || '—'}`;

  const onExcel = () => { const cols = COLUMNS.map((col) => ({ field: col.key,
      headerName: col.label,
      valueGetter: ({ row }) => (col.fmt === fmtDate ? fmtDate(row[col.key]) : row[col.key]) }));
    exportToExcel(exportRows, cols, `${REPORT_CODE}_st_rut_tanim_listesi`); };

  return (
    <div style={l.wrap}>
      <div style={l.topbar}>
        <div style={l.topLeft}>
          <span style={l.titleIcon}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="4" width="3" height="14" /></svg>
          </span>
          <div>
            <div style={l.title}>{REPORT_TITLE}</div>
            <div style={l.subtitle}>{loading ? 'Yükleniyor…' : `${fmtInt(rutSayisi)} rut · ${fmtInt(rows.length)} satır · ${nowLabel}`}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={l.iconBtn} title="PDF İndir" onClick={() => printAsPdf({ rows: exportRows, subtitle: summaryText })}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15h1.5a1.5 1.5 0 0 0 0-3H9v6" /><path d="M15.5 12H14v6" /><path d="M14 15h1.5" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Excel'e Aktar" onClick={onExcel}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16794c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 3 3 3-3" /><path d="M12 12v6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Yazdır" onClick={() => window.print()}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          </button>
        </div>
      </div>

      <div style={l.critBar}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #94a3b8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
        <span style={{ fontWeight: 800, color: 'var(--text-secondary, #64748b)' }}>KRİTERLER</span>
        <span style={l.critChip}>Esas Alınacak Tarih: {fmtDate(params.refDate)}</span>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 20px 20px' }}>
        {error && <div style={{ ...l.empty, color: '#dc2626' }}>{error}</div>}
        {!error && !loading && (
          <ReportGrid
            reportKey="route-definition"
            columns={COLUMNS}
            rows={rows}
            onVisibleRowsChange={setVisibleRows}
          />
        )}
        {loading && <div style={l.empty}>Yükleniyor…</div>}
      </div>
    </div>
  ); }
